// ═══════════════════════════════════════════════════════════════════════
// useSoporte — Hook de lógica de soporte técnico y BCM
// ═══════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { SOPORTE_STORAGE_KEY, API_URL } from '../constants';
import { authFetch } from '../utils';

export default function useSoporte({
  conversacionActiva,
  estadoOperativoBackend,
  guardarEstadoOperativo,
  agentes,
  agenteActualId,
  currentUser,
  cargarConversaciones,
  limpiarConversacionActiva,
}) {
  const [soporteLocalData, setSoporteLocalData] = useState({});
  const [diagnosticoBCM, setDiagnosticoBCM] = useState(null);
  const [ejecutandoAccionBCM, setEjecutandoAccionBCM] = useState(false);
  const [mensajeBCM, setMensajeBCM] = useState(null);
  const [reinicioConfirmado, setReinicioConfirmado] = useState(null);

  useEffect(() => {
    try { const saved = localStorage.getItem(SOPORTE_STORAGE_KEY); if (saved) setSoporteLocalData(JSON.parse(saved)); } catch (e) { console.error('Error leyendo soporte local storage:', e); }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(SOPORTE_STORAGE_KEY, JSON.stringify(soporteLocalData)); } catch (e) { console.error('Error guardando soporte local storage:', e); }
  }, [soporteLocalData]);

  const actualizarSoporteLocal = (convId, patch) => {
    if (!convId) return;
    setSoporteLocalData((prev) => ({ ...prev, [convId]: { ...prev[convId], ...patch } }));
  };

  const obtenerSoporteLocal = (conv) => {
    if (!conv?.id) return null;
    const saved = soporteLocalData[conv.id] || {};
    return {
      estadoTecnico: saved.estadoTecnico || 'Pendiente de revisión',
      diagnostico: saved.diagnostico || 'Sin señal óptica',
      proximaAccion: saved.proximaAccion || 'Revisar ONU y validar visita',
      visitaTecnica: saved.visitaTecnica || 'No',
      observacion: saved.observacion || '',
    };
  };

  const obtenerSoporte = (conv) => {
    const local = obtenerSoporteLocal(conv);
    if (!conv?.id) return local;
    return {
      estadoTecnico: estadoOperativoBackend?.estado_soporte || local?.estadoTecnico || 'Pendiente de revisión',
      diagnostico: local?.diagnostico || 'Sin señal óptica',
      proximaAccion: estadoOperativoBackend?.proxima_accion || local?.proximaAccion || 'Revisar ONU y validar visita',
      visitaTecnica: typeof estadoOperativoBackend?.visita_tecnica === 'boolean'
        ? estadoOperativoBackend.visita_tecnica ? 'Sí' : 'No'
        : local?.visitaTecnica || 'No',
      observacion: estadoOperativoBackend?.observacion || local?.observacion || '',
    };
  };

  const soporteActual = obtenerSoporte(conversacionActiva);

  // ── Acciones BCM ─────────────────────────────────────────────────────

  const ejecutarDiagnostico = async () => {
    if (!conversacionActiva?.id || ejecutandoAccionBCM) return;
    try {
      setEjecutandoAccionBCM(true);
      setMensajeBCM('Consultando BCM...');
      setDiagnosticoBCM(null);
      const resp = await authFetch(`${API_URL}/api/dashboard/bcm/diagnostico/${conversacionActiva.id}`);
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) { setMensajeBCM(`❌ ${data?.error || 'Error consultando BCM'}`); return; }
      setDiagnosticoBCM(data.diagnostico);
      actualizarSoporteLocal(conversacionActiva.id, { estadoTecnico: 'Diagnóstico en curso', diagnostico: data.diagnostico?.diagnostico_resumen || 'Sin datos' });
      await guardarEstadoOperativo({
        area: 'soporte', campo: 'estado_soporte', valor: 'Diagnóstico en curso',
        proxima_accion: data.diagnostico?.problema_fisico ? 'Problema físico — generar ticket' : 'Evaluar reinicio o desbloqueo',
        observacion: data.diagnostico?.diagnostico_resumen || null,
      });
      setMensajeBCM(data.diagnostico?.problema_fisico
        ? '⚠️ ONU sin potencia óptica (ESTADO=0). Problema físico. Generá ticket ISPCube.'
        : `✅ ONU online. ${data.diagnostico?.diagnostico_resumen || ''}`);
    } catch (e) { setMensajeBCM('❌ Error de conexión con BCM'); }
    finally { setEjecutandoAccionBCM(false); }
  };

  const reiniciarONU = async () => {
    if (!conversacionActiva?.id || ejecutandoAccionBCM) return;
    const serial = diagnosticoBCM?.serial;
    if (!serial) { alert('❌ Ejecutá primero el diagnóstico para obtener el serial de la ONU.'); return; }
    if (!window.confirm(`¿Reiniciar ONU remotamente?\n\nSerial: ${serial}\n\nEl equipo tardará 2-3 minutos en volver.`)) return;
    try {
      setEjecutandoAccionBCM(true);
      setReinicioConfirmado(null);
      let fechaAntes = null;
      try {
        const respAntes = await authFetch(`${API_URL}/api/dashboard/bcm/estado-serial/${serial}`);
        const dataAntes = await respAntes.json().catch(() => ({}));
        fechaAntes = dataAntes?.FECHA_POTENCIA || null;
      } catch (e) {}
      setMensajeBCM('Enviando reinicio ONU...');
      const resp = await authFetch(`${API_URL}/api/dashboard/bcm/reiniciar/${conversacionActiva.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serial, agente_id: agenteActualId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) { setMensajeBCM(`❌ ${data?.error || 'Error reiniciando ONU'}`); return; }
      setMensajeBCM('⏳ Reinicio enviado. Verificando reconexión en 3 minutos...');
      setTimeout(async () => {
        try {
          setMensajeBCM('🔍 Verificando reconexión...');
          const respDespues = await authFetch(`${API_URL}/api/dashboard/bcm/estado-serial/${serial}`);
          const dataDespues = await respDespues.json().catch(() => ({}));
          const fechaDespues = dataDespues?.FECHA_POTENCIA || null;
          const estadoDespues = dataDespues?.ESTADO;
          if (fechaDespues && fechaAntes && fechaDespues !== fechaAntes) {
            setReinicioConfirmado({ confirmado: true, fecha: fechaDespues, potencia: dataDespues?.POTENCIA });
            setMensajeBCM(`✅ Reinicio confirmado. Reconectó a las ${fechaDespues.split(' ')[1]}`);
          } else if (estadoDespues === '1' || estadoDespues === 1) {
            setReinicioConfirmado({ confirmado: true, fecha: fechaDespues, potencia: dataDespues?.POTENCIA });
            setMensajeBCM(`✅ ONU online. Potencia: ${dataDespues?.POTENCIA || '-'} dBm`);
          } else {
            setReinicioConfirmado({ confirmado: false, fecha: fechaDespues });
            setMensajeBCM('⚠️ Sin reconexión todavía. Podés verificar manualmente.');
          }
        } catch (e) { setMensajeBCM('⚠️ Reinicio enviado. No se pudo verificar reconexión automáticamente.'); }
      }, 3 * 60 * 1000);
    } catch (e) { setMensajeBCM('❌ Error de conexión'); }
    finally { setEjecutandoAccionBCM(false); }
  };

  const verificarReinicioManual = async () => {
    const serial = diagnosticoBCM?.serial;
    if (!serial) return;
    try {
      setMensajeBCM('🔍 Verificando estado ONU...');
      const resp = await authFetch(`${API_URL}/api/dashboard/bcm/estado-serial/${serial}`);
      const data = await resp.json().catch(() => ({}));
      if (data?.ESTADO === '1' || data?.ESTADO === 1) {
        setReinicioConfirmado({ confirmado: true, fecha: data.FECHA_POTENCIA, potencia: data.POTENCIA });
        setMensajeBCM(`✅ ONU online. Potencia: ${data.POTENCIA || '-'} dBm. Última reconexión: ${data.FECHA_POTENCIA || '-'}`);
      } else { setMensajeBCM('⚠️ ONU todavía sin reconectar.'); }
    } catch (e) { setMensajeBCM('❌ Error verificando estado'); }
  };

  const desbloquearBCM = async () => {
    if (!conversacionActiva?.id || ejecutandoAccionBCM) return;
    if (!window.confirm('¿Desbloquear cliente en BCM?\n\nSolo se ejecuta si ISPCube ya lo tiene habilitado.')) return;
    try {
      setEjecutandoAccionBCM(true);
      setMensajeBCM('Verificando estado en ISPCube...');
      const resp = await authFetch(`${API_URL}/api/dashboard/bcm/desbloquear/${conversacionActiva.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agente_id: agenteActualId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 409) { setMensajeBCM(`🔒 ${data?.mensaje || 'Cliente todavía bloqueado en ISPCube.'}`); return; }
      if (!resp.ok) { setMensajeBCM(`❌ ${data?.error || 'Error desbloqueando en BCM'}`); return; }
      setMensajeBCM(data.mensaje || '✅ Cliente desbloqueado en BCM.');
      await guardarEstadoOperativo({ area: 'soporte', campo: 'estado_soporte', valor: 'Diagnóstico en curso', proxima_accion: 'Confirmar conexión con el cliente', observacion: `Desbloqueado en BCM. ISPCube status=${data.status_ispcube}` });
    } catch (e) { setMensajeBCM('❌ Error de conexión'); }
    finally { setEjecutandoAccionBCM(false); }
  };

  const confirmarSoporte = async () => {
    if (!conversacionActiva?.id || ejecutandoAccionBCM) return;
    if (!agenteActualId) { alert('No hay agentes disponibles'); return; }
    const problema = soporteActual?.diagnostico || prompt('Describí el problema técnico:');
    if (!problema) return;
    if (!window.confirm(`¿Generar ticket de soporte en ISPCube y cerrar conversación?\n\nProblema: ${problema}`)) return;
    try {
      setEjecutandoAccionBCM(true);
      setMensajeBCM('Generando ticket en ISPCube...');
      const resp = await authFetch(`${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/confirmar-soporte`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agente_id: agenteActualId,
          agente_nombre: agentes.find((a) => a.id === agenteActualId)?.nombre || currentUser?.nombre || 'Agente',
          problema, observaciones: soporteActual?.observacion || null,
          serial: diagnosticoBCM?.serial || null, idDeviceTR: diagnosticoBCM?.idDeviceTR || null,
          estado_onu: diagnosticoBCM?.estado_onu ?? null, rx_power: diagnosticoBCM?.rx_power || null,
          diagnostico_resumen: diagnosticoBCM?.diagnostico_resumen || null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) { setMensajeBCM(`❌ ${data?.error || 'Error generando ticket'}`); alert(`❌ ${data?.error || 'No se pudo generar el ticket'}`); return; }
      const ref = data.ticket?.referencia;
      alert(`✅ Ticket generado${ref ? ` #${ref}` : ''}. Conversación cerrada.`);
      setDiagnosticoBCM(null); setMensajeBCM(null);
      limpiarConversacionActiva();
      await cargarConversaciones();
    } catch (e) { setMensajeBCM('❌ Error de conexión'); }
    finally { setEjecutandoAccionBCM(false); }
  };

  const accionRapidaSoporte = async (accion) => {
    if (!conversacionActiva?.id) return;
    const map = {
      diagnostico: { estadoTecnico: 'Diagnóstico en curso', proximaAccion: 'Validar mediciones y descartar falla física', visitaTecnica: 'No' },
      visita: { estadoTecnico: 'Derivado a visita técnica', proximaAccion: 'Coordinar visita con cliente', visitaTecnica: 'Sí' },
      resuelto: { estadoTecnico: 'Resuelto', proximaAccion: 'Sin acción pendiente', visitaTecnica: 'No' },
    };
    const next = map[accion];
    if (!next) return;
    actualizarSoporteLocal(conversacionActiva.id, { estadoTecnico: next.estadoTecnico, proximaAccion: next.proximaAccion, visitaTecnica: next.visitaTecnica });
    await guardarEstadoOperativo({
      area: 'soporte', campo: 'estado_soporte', valor: next.estadoTecnico,
      proxima_accion: next.proximaAccion, visita_tecnica: next.visitaTecnica === 'Sí',
      observacion: obtenerSoporte(conversacionActiva)?.observacion || null,
    });
  };

  // Limpiar BCM al cambiar de conversación
  const resetBCM = () => { setDiagnosticoBCM(null); setMensajeBCM(null); };

  return {
    soporteLocalData, diagnosticoBCM, ejecutandoAccionBCM, mensajeBCM, reinicioConfirmado,
    soporteActual, actualizarSoporteLocal, obtenerSoporteLocal, obtenerSoporte,
    ejecutarDiagnostico, reiniciarONU, verificarReinicioManual, desbloquearBCM, confirmarSoporte,
    accionRapidaSoporte, resetBCM,
  };
}
